import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { enableAdminPwa } from "@/admin/pwa";
import { api, errMsg } from "@/lib/api";

export default function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { enableAdminPwa(); }, []);
  const qc = useQueryClient();

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const login = (e) => { e.preventDefault(); run(async () => setMfa((await api.post("/admin/auth/login", { email, password })).data)); };
  const verify = (e) => {
    e.preventDefault();
    run(async () => {
      try {
        await api.post("/admin/auth/verify", { mfa_token: mfa.mfa_token, code });
      } catch (err) {
        if (err.response?.status !== 401 || !/expired|invalid token/i.test(errMsg(err))) throw err;
        const fresh = (await api.post("/admin/auth/login", { email, password })).data;
        setMfa((m) => ({ ...fresh, setup_required: m.setup_required, totp_secret: m.totp_secret, otpauth_uri: m.otpauth_uri }));
        await api.post("/admin/auth/verify", { mfa_token: fresh.mfa_token, code });
      }
      qc.invalidateQueries({ queryKey: ["paycfg"] });
      const next = new URLSearchParams(window.location.search).get("next") || "";
      nav(next.startsWith("/admin") && !next.startsWith("/admin/login") ? next : "/admin");
    });
  };

  return (
    <div className="admin-dark flex min-h-screen items-center justify-center bg-background px-4 text-foreground">
      <div className="w-full max-w-md rounded-md border border-border bg-card p-8" data-testid="admin-login-card">
        <div className="flex items-center gap-3">
          <img src="/namonics-logo-sm.png" alt="Namonics logo" className="h-12 w-12 rounded-full" />
          <div>
            <p className="font-serif-display text-2xl">Namonics™ Admin</p>
            <p className="text-xs text-muted-foreground">Authorised administrators only</p>
          </div>
        </div>
        {!mfa ? (
          <form onSubmit={login} className="mt-8 space-y-4">
            <Input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-email-input" />
            <Input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-password-input" />
            <button disabled={busy} className="w-full rounded bg-primary py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/85" data-testid="admin-login-btn">{busy ? "Checking…" : "Continue"}</button>
            <p className="pt-2 text-center text-[11px] leading-relaxed text-muted-foreground" data-testid="admin-install-hint">On your phone, add this page to your home screen for one-tap access — iPhone: Safari › Share › Add to Home Screen · Android: Chrome › ⋮ › Install app.</p>
          </form>
        ) : (
          <form onSubmit={verify} className="mt-8 space-y-5">
            {mfa.setup_required && (
              <div className="rounded border border-border p-4 text-sm" data-testid="totp-setup">
                <p className="mb-3 text-muted-foreground">First sign-in: scan this QR code with an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password).</p>
                <div className="inline-block rounded bg-white p-3"><QRCodeSVG value={mfa.otpauth_uri} size={150} /></div>
                <p className="mt-3 break-all font-mono-ui text-xs text-muted-foreground" data-testid="totp-secret">Key: {mfa.totp_secret}</p>
              </div>
            )}
            <Input inputMode="numeric" maxLength={6} placeholder="6-digit authentication code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required className="text-center font-mono-ui text-lg tracking-[0.4em] placeholder:text-sm placeholder:tracking-normal" data-testid="admin-totp-input" autoFocus />
            <button disabled={busy || code.length !== 6} className="w-full rounded bg-primary py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/85 disabled:opacity-40" data-testid="admin-verify-btn">{busy ? "Verifying…" : "Verify & sign in"}</button>
            <p className="text-center text-xs text-muted-foreground">Enter the code currently shown in your authenticator app — codes refresh every 30 seconds.</p>
            <button type="button" onClick={() => { setMfa(null); setCode(""); }} className="w-full text-xs text-muted-foreground hover:text-foreground" data-testid="admin-login-back-btn">← Back to email & password</button>
          </form>
        )}
      </div>
    </div>
  );
}
